import { HardDrive, ShieldCheck, ShieldX } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { StorageEstimate } from '@obraplan/core/application/ports/storage-inspector';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingState } from '@/components/feedback/states';
import { storagePressure } from '@obraplan/core/domain/storage/thresholds';
import { formatBytes, formatPercent } from '@/lib/format';
import { useContainer } from '@/features/session/use-session';

/**
 * A cota do navegador.
 *
 * Desde a Fase 32 o navegador guarda só a sessão e a obra aberta — os dados, as fotos e as
 * planilhas moram no servidor. A tela continua porque a cota é do aparelho: um navegador
 * sem espaço, ou que apaga dados de sites por conta própria, ainda pode derrubar a sessão.
 * A medição por tabela, o expurgo das planilhas e o arquivamento das fotos saíram com o
 * banco que mediam.
 */
export function StoragePage() {
  const container = useContainer();
  const [estimate, setEstimate] = useState<StorageEstimate | undefined>(undefined);
  // "Ainda não pedi" e "pedi e me disseram não" são diferentes: só o segundo esconde o
  // botão, para o usuário não clicar para sempre no pedido que já respondeu não.
  const [persistenceDenied, setPersistenceDenied] = useState(false);

  const refresh = useCallback(async () => {
    setEstimate(await container.getStorageStatus.execute());
  }, [container]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requestPersistence = async () => {
    const granted = await container.requestStoragePersistence.execute();
    setPersistenceDenied(!granted);
    await refresh();
  };

  if (estimate === undefined) {
    return <LoadingState label="Consultando o armazenamento…" />;
  }

  const ratio = estimate.usedRatio;
  const pressure = storagePressure(ratio);
  const tone = pressure === 'block' ? 'danger' : pressure === 'warn' ? 'risk' : 'ok';

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Armazenamento</h1>
        <p className="text-(--color-content-muted)">
          Neste navegador ficam só a sessão e a obra aberta; os dados moram no servidor.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Espaço utilizado</CardTitle>
            <CardDescription>
              O consumo do site inteiro neste navegador, inclusive o que ele guarda em cache.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {!estimate.supported ? (
              <p className="text-sm text-(--color-content-muted)">
                Este navegador não informa o consumo de armazenamento.
              </p>
            ) : (
              <>
                <p className="text-3xl font-semibold tabular">
                  {formatBytes(estimate.usageBytes)}
                  <span className="text-base font-normal text-(--color-content-muted)">
                    {' de '}
                    {formatBytes(estimate.quotaBytes)}
                  </span>
                </p>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-(--color-surface-muted)"
                  role="meter"
                  aria-valuenow={Math.round(ratio * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Consumo do armazenamento"
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: Math.min(100, Math.max(2, ratio * 100)) + '%',
                      backgroundColor:
                        tone === 'danger'
                          ? 'var(--color-state-danger)'
                          : tone === 'risk'
                            ? 'var(--color-state-risk)'
                            : 'var(--color-state-ok)',
                    }}
                  />
                </div>
                <Badge tone={tone} icon={<HardDrive aria-hidden="true" />}>
                  {formatPercent(ratio)} utilizado
                </Badge>
              </>
            )}
            <Button variant="secondary" size="sm" onClick={() => void refresh()}>
              Atualizar
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Durabilidade</CardTitle>
            <CardDescription>
              Navegadores podem apagar dados de sites automaticamente quando o disco enche.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-start gap-3">
            {estimate.persisted ? (
              <Badge tone="ok" icon={<ShieldCheck aria-hidden="true" />}>
                Armazenamento persistente concedido
              </Badge>
            ) : (
              <>
                <Badge tone="warn" icon={<ShieldX aria-hidden="true" />}>
                  Armazenamento não persistente
                </Badge>
                <p className="text-sm text-(--color-content-muted)">
                  Sem persistência, o navegador pode apagar a sessão sem aviso, e será preciso
                  entrar de novo.
                </p>
                {persistenceDenied ? (
                  <p role="alert" className="text-sm text-(--color-state-risk)">
                    Este navegador negou o pedido. Ele costuma conceder a persistência depois que o
                    site é usado com frequência ou instalado.
                  </p>
                ) : (
                  <Button variant="primary" size="sm" onClick={() => void requestPersistence()}>
                    Solicitar armazenamento persistente
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
